import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MessageCircle,
  Pin,
  Plus,
  Archive,
  ArchiveRestore,
} from "lucide-react";
import { parseISO } from "date-fns";
import { relativeDistance } from "@/lib/dates";
import { Card, SectionLabel } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { listThreads, updateThread } from "@/api/chat";
import { cn } from "@/lib/utils";

export default function ChatList() {
  const { t } = useTranslation();
  const [showArchived, setShowArchived] = useState(false);
  const nav = useNavigate();
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ["chat-threads", showArchived],
    queryFn: () => listThreads(showArchived),
    staleTime: 10_000,
  });

  const newThread = () => {
    nav("/chat/new");
  };

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <header className="flex items-start justify-between">
        <div>
          <SectionLabel>{t("coach.section_title")}</SectionLabel>
          <h1 className="mt-1 font-serif text-[26px] font-medium leading-snug md:text-[30px]">
            {t("coach.conversations_h1")}
          </h1>
          <p className="mt-1 text-[13px] text-taupe">
            {t("coach.conversations_desc")}
          </p>
        </div>
        <Button onClick={newThread} className="inline-flex items-center gap-1">
          <Plus className="h-4 w-4" strokeWidth={2} />
          {t("coach.new_thread")}
        </Button>
      </header>

      <div className="flex gap-2 border-b border-cream-border pb-1">
        <button
          type="button"
          onClick={() => setShowArchived(false)}
          className={cn(
            "border-b-2 px-3 py-2 text-[13px] font-medium transition-colors",
            !showArchived
              ? "border-amber-dark text-navy-deep"
              : "border-transparent text-taupe hover:text-navy-deep",
          )}
        >
          {t("coach.tab_active")}
        </button>
        <button
          type="button"
          onClick={() => setShowArchived(true)}
          className={cn(
            "border-b-2 px-3 py-2 text-[13px] font-medium transition-colors",
            showArchived
              ? "border-amber-dark text-navy-deep"
              : "border-transparent text-taupe hover:text-navy-deep",
          )}
        >
          {t("coach.tab_archived")}
        </button>
      </div>

      {q.isLoading ? (
        <div className="py-10 text-center text-taupe">{t("week_detail.loading")}</div>
      ) : (q.data ?? []).length === 0 ? (
        <Card>
          <div className="py-8 text-center">
            <MessageCircle
              className="mx-auto mb-3 h-8 w-8 text-taupe/60"
              strokeWidth={1.5}
            />
            <p className="text-[14px] font-medium text-navy-deep">
              {showArchived ? t("coach.empty_archive", { defaultValue: "Archive empty" }) : t("coach.empty_threads", { defaultValue: "No conversations yet" })}
            </p>
            {!showArchived ? (
              <p className="mx-auto mt-1 max-w-md text-[13px] text-taupe">
                {t("coach.empty_threads_hint", { defaultValue: "Click «New thread» — ask the coach anything, attach a photo." })}
              </p>
            ) : null}
          </div>
        </Card>
      ) : (
        <ul className="space-y-2">
          {(q.data ?? []).map((th) => (
            <li key={th.id}>
              <Link
                to={`/chat/${th.id}`}
                className="block rounded-xl border border-cream-border bg-cream-light p-4 transition-colors hover:border-amber/40"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      {th.pinned ? (
                        <Pin
                          className="h-3.5 w-3.5 text-amber-dark"
                          strokeWidth={2}
                        />
                      ) : null}
                      <span className="truncate font-medium text-[15px] text-navy-deep">
                        {th.title}
                      </span>
                    </div>
                    {th.last_message_preview ? (
                      <p className="mt-1 truncate text-[12px] text-taupe">
                        {th.last_message_preview}
                      </p>
                    ) : null}
                    <div className="mt-2 flex items-center gap-2 text-[11px] text-taupe/80">
                      <span>{th.message_count}</span>
                      {th.last_message_at ? (
                        <>
                          <span>·</span>
                          <span>
                            {relativeDistance(th.last_message_at)}
                          </span>
                        </>
                      ) : null}
                    </div>
                  </div>
                  <ThreadActions
                    thread={th}
                    onUpdated={() =>
                      qc.invalidateQueries({ queryKey: ["chat-threads"] })
                    }
                  />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ThreadActions({
  thread,
  onUpdated,
}: {
  thread: { id: number; pinned: boolean; archived: boolean };
  onUpdated: () => void;
}) {
  const { t } = useTranslation();
  const toggle = async (patch: { pinned?: boolean; archived?: boolean }) => {
    await updateThread(thread.id, patch);
    onUpdated();
  };
  return (
    <div
      className="flex shrink-0 gap-1"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <button
        type="button"
        onClick={() => toggle({ pinned: !thread.pinned })}
        className="rounded-md border border-cream-border bg-cream p-1.5 text-taupe hover:text-amber-dark"
        title={thread.pinned ? t("coach.unpin", { defaultValue: "Unpin" }) : t("coach.pin", { defaultValue: "Pin" })}
      >
        <Pin className="h-3.5 w-3.5" strokeWidth={1.8} />
      </button>
      <button
        type="button"
        onClick={() => toggle({ archived: !thread.archived })}
        className="rounded-md border border-cream-border bg-cream p-1.5 text-taupe hover:text-navy-deep"
        title={thread.archived ? t("coach.restore", { defaultValue: "Restore" }) : t("coach.archive_action", { defaultValue: "Archive" })}
      >
        {thread.archived ? (
          <ArchiveRestore className="h-3.5 w-3.5" strokeWidth={1.8} />
        ) : (
          <Archive className="h-3.5 w-3.5" strokeWidth={1.8} />
        )}
      </button>
    </div>
  );
}
